import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import { $user } from "../helper/UserFactory";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { EMAIL_REGEX } from "../helper/constants";
import { useDispatch } from "react-redux";
import { RIGHT_TOP, SET_OPEN_SIDE_BAR, SET_USER_LOGIN_TOKEN, SET_SCREEN_TYPE } from "../store";
import { queueStartupNavigation, isDeeplinkPath } from "../utils/startupScreen";
import { translate } from "../Languages/index";
import { SUPPORTED_LANGUAGE_CODES, getBrowserLanguage, isGermanFamily } from "./auth/authLanguages";
import { AuthEye, AuthFrame, AuthHead, AuthInput, AuthFieldMessage, AuthNotice, AuthOr, AuthSpacer, AuthTitle } from "./auth/AuthPro";

import '../styles/pages.css';

const TOKEN_KEYS = ['firebaseToken', 'uniqueDeviceId'];
const SCREEN_MOBILE_THRESHOLD = 860;

const detectScreen = (dispatch) => {
  const isMobile = window.innerWidth <= SCREEN_MOBILE_THRESHOLD;
  if (process.env.NODE_ENV === 'development') {
    console.log('Login page screen detection - width:', window.innerWidth, 'isMobile:', isMobile);
  }
  dispatch({ type: SET_SCREEN_TYPE, payload: !isMobile });
};

const updateLanguageState = (langCode, setIsGerm, setIsGerms, setLanguage, setContextLanguage) => {
  // langCode can be a language code string or a boolean for backward compatibility
  let normalizedLang;
  if (typeof langCode === 'boolean') {
    // Backward compatibility: true = 'de', false = 'en'
    normalizedLang = langCode ? 'de' : 'en';
  } else {
    normalizedLang = (langCode || 'en').toLowerCase();
  }
  
  // Validate language code - check against all supported languages
  if (!SUPPORTED_LANGUAGE_CODES.includes(normalizedLang)) {
    normalizedLang = 'en';
  }
  
  const isGerman = normalizedLang === 'de' || normalizedLang === 'gsw' || normalizedLang === 'wae';
  setIsGerm(isGerman);
  setIsGerms(isGerman);
  setLanguage(normalizedLang.toUpperCase());
  
  // Set localStorage values
  localStorage.setItem("switch", isGerman ? "2" : "1");
  localStorage.setItem("language", normalizedLang);
  
  // Update context language if available
  if (setContextLanguage) {
    setContextLanguage(normalizedLang);
  }
};

function Login() {
  const [params, setParams] = useState({
    email: "",
    password: "",
  });
  const { isGerm, setIsGerm, strings, setLanguage: setContextLanguage, language: contextLanguage } = React.useContext(LanguageContext);
  const dispatch = useDispatch();
  const location = useLocation();
  const [verifyEmailPending, setVerifyEmailPending] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  
  // Initialize local language state from stored preference (survives logout), falling back to browser language
  const [language, setLanguage] = React.useState(() => {
    const stored = localStorage.getItem('language');
    if (stored && SUPPORTED_LANGUAGE_CODES.includes(stored)) {
      return stored.toUpperCase();
    }
    return getBrowserLanguage().toUpperCase();
  });
  const [isGerms, setIsGerms] = useState(() => {
    const stored = localStorage.getItem('language');
    const lang = stored || getBrowserLanguage();
    return lang === 'de' || lang === 'gsw' || lang === 'wae';
  });

  const timeoutRefs = useRef([]);
  const isWebView = typeof window !== 'undefined' && window.ReactNativeWebView;

  const sendTokenStatus = useCallback((success = true, error = null) => {
    if (!isWebView) return;
    const message = error 
      ? { type: 'TOKEN_STATUS', success: false, error }
      : { type: 'TOKEN_STATUS', success: true };
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  }, [isWebView]);

  const addTimeout = useCallback((callback, delay) => {
    const timeout = setTimeout(callback, delay);
    timeoutRefs.current.push(timeout);
    return timeout;
  }, []);

  useEffect(() => {
    document.body.classList.add('login-page-active');

    // Restore stored language preference (persisted across logout) or fall back to browser language
    const storedLang = localStorage.getItem('language');
    const regiLang = localStorage.getItem('regiLang');
    if (storedLang && SUPPORTED_LANGUAGE_CODES.includes(storedLang)) {
      updateLanguageState(storedLang, setIsGerm, setIsGerms, setLanguage, setContextLanguage);
    } else if (!regiLang) {
      const browserLang = getBrowserLanguage();
      updateLanguageState(browserLang, setIsGerm, setIsGerms, setLanguage, setContextLanguage);
    }
    
    // Mark icons as loaded after a short delay to trigger animation
    // Use a slightly longer delay to ensure CSS is loaded first
    addTimeout(() => {
      setIconsLoaded(true);
    }, 100);
    
    addTimeout(() => {
      if (isWebView) {
        window.ReactNativeWebView.postMessage('{"type":"WEB_VIEW_READY"}');
      }
    }, 1000);
    
    const existingToken = localStorage.getItem(TOKEN_KEYS[0]);
    const existingDeviceId = localStorage.getItem(TOKEN_KEYS[1]);
    
    if (process.env.NODE_ENV === 'development') {
      if (existingToken) console.log("Firebase token found:", `${existingToken.substring(0, 10)}...`);
      if (existingDeviceId) console.log("Device ID found:", `${existingDeviceId.substring(0, 10)}...`);
    }
    
    if (existingToken && existingDeviceId) {
      addTimeout(() => sendTokenStatus(), 500);
    }
    
    const handleMessage = (event) => {
      try {
        if (typeof event.data !== 'string' || !event.data.startsWith('{') || !event.data.endsWith('}')) {
          return;
        }
        
        const data = JSON.parse(event.data);
        
        if (data.firebaseToken) {
          localStorage.setItem(TOKEN_KEYS[0], data.firebaseToken);
          sendTokenStatus();
        }
        
        if (data.uniqueDeviceId) {
          localStorage.setItem(TOKEN_KEYS[1], data.uniqueDeviceId);
        }
      } catch (e) {
        console.error('Error processing message from WebView:', e);
        sendTokenStatus(false, e.message);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (TOKEN_KEYS.includes(key)) {
        addTimeout(() => sendTokenStatus(), 100);
      }
    };
    
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
      localStorage.setItem = originalSetItem;
      document.body.classList.remove('login-page-active');
      window.removeEventListener('message', handleMessage);
    };
  }, [isWebView, sendTokenStatus, addTimeout]);

  useEffect(() => {
    let pendingEmail = "";
    try {
      pendingEmail =
        (location.state && location.state.registeredEmail) ||
        sessionStorage.getItem("kjuupVerifyEmail") ||
        "";
    } catch (e) {
      pendingEmail = (location.state && location.state.registeredEmail) || "";
    }
    const pending = Boolean(
      (location.state && location.state.verifyEmailPending) || pendingEmail
    );
    if (!pending) return;
    setVerifyEmailPending(true);
    if (pendingEmail) {
      setParams((prev) => ({ ...prev, email: pendingEmail }));
      try {
        sessionStorage.setItem("kjuupVerifyEmail", pendingEmail);
      } catch (e) {}
    }
  }, [location.state]);

  const [loading, setLoading] = useState(false);
  // Inline error (mobile Pro sign in): { field: 'email' | 'password' | null, message }, cleared by typing.
  const [authError, setAuthError] = useState(null);
  const history = useHistory();
  const [isReactive, setIsReactive] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [iconsLoaded, setIconsLoaded] = useState(false);

  const sendWebViewMessage = useCallback((message) => {
    if (isWebView) {
      window.ReactNativeWebView.postMessage(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, [isWebView]);

  const setParam = useCallback((name, value) => {
    setParams(prev => ({ ...prev, [name]: value }));
    setAuthError(null);
  }, []);

  const handleEmailChange = useCallback((e) => setParam("email", e.target.value), [setParam]);
  const handlePasswordChange = useCallback((e) => setParam("password", e.target.value), [setParam]);

  const t = useCallback((key) => translate(key, language.toLowerCase()), [language]);

  const resendConfirmationEmail = useCallback(async () => {
    const email = (params.email || "").trim();
    if (!email) {
      setResendMsg(translate("invalid_fiels", language.toLowerCase()));
      return;
    }
    setResendLoading(true);
    setResendMsg("");
    try {
      const data = await $crud.post(
        "/auth/resendVerification",
        { email, lang: language.toLowerCase() },
        { notify: false }
      );
      setResendMsg(
        (data && data.message) ||
          translate("resend_confirmation_sent", language.toLowerCase())
      );
    } catch (e) {
      setResendMsg(e.message || translate("resend_confirmation_sent", language.toLowerCase()));
    }
    setResendLoading(false);
  }, [params.email, language]);

  // An empty field gets the design's "Check your e-mail and password" line, on that field.
  const handleValidation = useCallback(() => {
    if (!params.email || !params.password) {
      setAuthError({ field: !params.email ? "email" : "password", message: t("pro_auth_check_credentials") });
      return false;
    }
    setAuthError(null);
    return true;
  }, [params.email, params.password, t]);

  const login = useCallback(async () => {
    const tokenx = localStorage.getItem(TOKEN_KEYS[0]);
    const deviceId = localStorage.getItem(TOKEN_KEYS[1]);
    
    sendWebViewMessage({
      type: 'LOGIN_DATA',
      data: {
        identifier: params.email,
        hasToken: !!tokenx,
        hasDeviceId: !!deviceId,
        isEmail: EMAIL_REGEX.test(params.email),
        timestamp: new Date().toISOString()
      }
    });
    
    sendWebViewMessage({
      type: 'DATA_USAGE',
      details: { action: 'login_attempt', hasToken: tokenx, hasDeviceId: deviceId }
    });
    
    setLoading(true);
    setAuthError(null);

    if (!handleValidation()) {
      setLoading(false);
      return;
    }

    try {
      // Get the selected language from dropdown (lowercase)
      const selectedLanguage = language.toLowerCase();
      
      const loginData = EMAIL_REGEX.test(params.email)
        ? { email: params.email, password: params.password, language: selectedLanguage }
        : { username: params.email, password: params.password, language: selectedLanguage };

      const res = await $user.login({ ...loginData, fcmId: tokenx, deviceId });
      
      sendWebViewMessage({
        type: 'LOGIN_RESULT',
        data: {
          success: !!res.status,
          isPinCheck: !!res.isPinCheck,
          requires2FA: !!res.requires2FA,
          isReactivation: !res.status && !!res.isPinCheck,
          hasError: !res.status && !res.isPinCheck && !res.requires2FA,
          errorMessage: !res.status && !res.requires2FA ? res.message : null,
          username: params.email,
          userId: res.status && res.user ? res.user.id : null,
          userLanguage: res.status && res.user ? res.user.language : null,
          timestamp: new Date().toISOString()
        }
      });
      
      sendWebViewMessage({
        type: 'DATA_USAGE',
        details: { action: 'login_result', success: !!res.status, tokensUsed: true }
      });
      
      if (!res.status) {
        if (res.requires2FA) {
          history.push({
            pathname: '/two-factor',
            state: { challenge: res }
          });
          setLoading(false);
          return;
        }
        if (res.isPinCheck) {
          setIsReactive(true);
        } else if (res.hasOwnProperty("isEnable")) {
          // Unconfirmed e-mail: the standing notice with the resend link (mobile Pro), no dialog.
          setVerifyEmailPending(true);
          setResendMsg("");
        } else if (res.isDeactivated || res.isActive === false) {
          // Refused for the account's state: the server's own reason, not the credentials line.
          setAuthError({ field: null, message: res.message });
        } else {
          setAuthError({ field: "email", message: t("pro_auth_check_credentials") });
        }
        setLoading(false);
        return;
      }

      // Use the selected language from dropdown to set interface language
      const selectedLang = language.toLowerCase();
      updateLanguageState(selectedLang, setIsGerm, setIsGerms, setLanguage, setContextLanguage);
      localStorage.removeItem("regiLang");
      try {
        sessionStorage.removeItem("kjuupVerifyEmail");
      } catch (e) {}
      
      detectScreen(dispatch);

      // Must queue BEFORE token dispatch: setting the token remounts the
      // logged-in app tree and unmounts Login (so history.push here is lost).
      // queueStartupNavigation keeps a captured /deeplink over Feed/Messages.
      const startup = queueStartupNavigation(res?.user?.startupScreen);
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: startup.sidebar });
      if (!isDeeplinkPath(startup.path)) {
        dispatch({ type: RIGHT_TOP, rightTop: '' });
      }
      dispatch({ type: SET_USER_LOGIN_TOKEN, status: res.token });
      setLoading(false);
    } catch (error) {
      console.error("Login error:", error);
      setLoading(false);
      setAuthError({ field: null, message: "An error occurred during login. Please try again." });
    }
  }, [params, language, handleValidation, sendWebViewMessage, dispatch, history, setIsGerm, setIsGerms, setLanguage, t]);

  const reactiveAccount = useCallback(() => {
    history.push({
      pathname: '/security-pin',
      state: {
        isResetAccount: true,
        username: params.email,
        password: params.password
      }
    });
  }, [history, params.email, params.password]);

  const handleClose = useCallback(() => {
    setIsReactive(false);
  }, []);

  const togglePassword = useCallback(() => {
    setViewPassword(prev => !prev);
  }, []);

  const handleChangeLanguage = useCallback((code) => {
    const langLower = String(code || 'en').toLowerCase();
    const germanFamily = isGermanFamily(langLower);
    
    // Update local state first
    setLanguage(langLower.toUpperCase());
    setIsGerm(germanFamily);
    setIsGerms(germanFamily);
    
    // Legacy support: store "2" for German-family, "1" for others
    localStorage.setItem("regiLang", germanFamily ? "2" : "1");
    localStorage.setItem("language", langLower);
    localStorage.setItem("switch", germanFamily ? "2" : "1");
    
    // Update context (drives strings via LANGUAGE_MAP)
    if (setContextLanguage) {
      setContextLanguage(langLower);
    }
  }, [setIsGerm, setIsGerms, setLanguage, setContextLanguage]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    login();
  }, [login]);

  useEffect(() => {
    detectScreen(dispatch);
  }, [dispatch]);

  // Phone keyboard: the browser only scrolls the focused field into view, so the page (its own scroll
  // area at the visible height) also brings Sign in above the keyboard, as far as the field stays in view.
  const signInRef = useRef(null);
  useEffect(() => {
    const vv = window.visualViewport;
    let timer = null;
    const reveal = () => {
      const button = signInRef.current;
      const page = button && button.closest(".kj-auth");
      const input = document.activeElement;
      if (!page || !input || input === button || !page.contains(input)) return;
      const field = input.closest(".kj-auth__field") || input;
      const box = page.getBoundingClientRect();
      const visTop = Math.max(box.top, vv ? vv.offsetTop : 0);
      const visBottom = Math.min(box.bottom, vv ? vv.offsetTop + vv.height : window.innerHeight);
      const hidden = button.getBoundingClientRect().bottom + 16 - visBottom;
      const by = Math.min(hidden, field.getBoundingClientRect().top - visTop - 8);
      if (by > 1) page.scrollBy({ top: by, behavior: "smooth" });
    };
    const later = () => {
      clearTimeout(timer);
      timer = setTimeout(reveal, 150);
    };
    document.addEventListener("focusin", later);
    if (vv) vv.addEventListener("resize", later);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", later);
      if (vv) vv.removeEventListener("resize", later);
    };
  }, []);

  const errorField = authError && authError.field;

  // Kjuup Pro · Sign in (mobile SignInPro, design 2b): head row (logo + language chip), "Welcome
  // back" (no subline), the e-mail and password fields with the inline error and "Forgot
  // password?", then Sign in, the "or" divider and Create account.
  return (
    <>
      <AuthFrame testId="signInPro">
        <AuthHead
          logo={process.env.PUBLIC_URL + "/assets/img/KJUUP-Schrift-Icon.png"}
          language={{ code: language.toLowerCase(), onChange: handleChangeLanguage, label: strings.language || "Language" }}
        />

        <AuthTitle title={t("pro_auth_signin_title")} />

        {verifyEmailPending && (
          // Standing notice, no dismiss: until the address is confirmed there is nothing else to do here.
          <AuthNotice tone="warn" icon="mark_email_unread" text={t("register_confirm_popup")}>
            <button
              type="button"
              className="kj-auth__link"
              disabled={resendLoading}
              onClick={resendConfirmationEmail}
            >
              {resendLoading ? t("please_wait") : t("resend_confirmation_email")}
            </button>
            {resendMsg && <p className="kj-auth__meta">{resendMsg}</p>}
          </AuthNotice>
        )}

        <form className="kj-auth__fields" onSubmit={handleSubmit} noValidate id="signInForm">
          <AuthInput
            id="signInEmail"
            aria-label={t("pro_auth_email_or_username")}
            value={params.email}
            onChange={handleEmailChange}
            placeholder={t("pro_auth_email_or_username")}
            error={errorField === "email"}
            type="text"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <AuthInput
            id="signInPassword"
            aria-label={strings.password}
            value={params.password}
            onChange={handlePasswordChange}
            placeholder={strings.password}
            error={errorField === "password"}
            type={viewPassword ? "text" : "password"}
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            trailing={
              <AuthEye
                shown={viewPassword}
                onToggle={togglePassword}
                showLabel={t("pro_auth_show_password")}
                hideLabel={t("pro_auth_hide_password")}
              />
            }
          />
          {authError && authError.message ? <AuthFieldMessage text={authError.message} /> : null}
          <Link to="/forgot-password" className="kj-auth__link">
            {t("pro_auth_forgot_password")}
          </Link>
        </form>

        <AuthSpacer />

        <div className="kj-auth__bottom">
          <button
            ref={signInRef}
            type="submit"
            form="signInForm"
            disabled={loading}
            aria-busy={loading}
            aria-label={loading ? strings.signing_in : undefined}
            className="kj-btn kj-btn--primary kj-btn--lg kj-btn--block kj-auth__btn"
          >
            {loading ? <Spinner animation="border" size="sm" /> : strings.sign_in}
          </button>
          <AuthOr label={strings.or} />
          <Link to="/register" className="kj-btn kj-btn--outline kj-btn--lg kj-btn--block kj-auth__btn">
            {t("pro_auth_create_account")}
          </Link>
        </div>
      </AuthFrame>

      <Modal show={isReactive} onHide={handleClose} centered>
        <Modal.Body>{strings.reactive_text}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="kj-btn kj-btn--outline kj-btn--sm" onClick={handleClose}>
            {strings.cancel}
          </button>
          <button type="button" className="kj-btn kj-btn--outline kj-btn--sm" onClick={reactiveAccount}>
            {strings.rac}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Login;
